import {
  useEffect, useLayoutEffect, useState, useCallback, useRef,
} from 'react';

/**
 * Hook to get the width of a referenced element, recalculates after resize.
 * Uses a callback ref so measurement starts when the element mounts, including
 * when it appears after a conditional render.
 * Width is rounded to an integer and committed at most once per animation frame
 * so ResizeObserver chatter does not remount table columns.
 * @returns {{ width: number, ref: (node: HTMLElement | null) => void }}
 */
export function useGetElementWidth() {
  const [width, setWidth] = useState(0);
  const [element, setElement] = useState(null);
  const widthRef = useRef(0);
  const rafRef = useRef(/** @type {number | null} */ (null));

  const ref = useCallback((node) => {
    setElement(node);
  }, []);

  const commitWidth = useCallback((nextWidth) => {
    const rounded = Math.round(nextWidth);
    if (rounded === widthRef.current) return;
    widthRef.current = rounded;
    setWidth(rounded);
  }, []);

  const updateWidth = useCallback(() => {
    if (!element) return;
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      commitWidth(element.offsetWidth);
    });
  }, [element, commitWidth]);

  useLayoutEffect(() => {
    if (!element) {
      widthRef.current = 0;
      setWidth(0);
      return undefined;
    }
    commitWidth(element.offsetWidth);
    return undefined;
  }, [element, commitWidth]);

  useEffect(() => {
    if (typeof window === 'undefined' || !element) return undefined;

    let cleanupObserver = () => {};
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(updateWidth);
      ro.observe(element);
      cleanupObserver = () => ro.disconnect();
    }

    window.addEventListener('resize', updateWidth);

    return () => {
      cleanupObserver();
      window.removeEventListener('resize', updateWidth);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [element, updateWidth]);

  return { width, ref };
}
